import React, { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import AsyncCreatableSelect from 'react-select/async-creatable';

export type AddressSuggestion = {
  streetLine: string;
  secondary?: string;
  city: string;
  state: string;
  zipcode: string;
};

type AddressOption = AddressSuggestion & {
  label: string;
  value: string;
};

interface AddressAutocompleteSelectProps {
  label?: string;
  isRequired?: boolean;
  errorText?: string;
  value: string;
  onTextChange: (text: string) => void;
  onSelectSuggestion: (suggestion: AddressSuggestion) => void;
  onBlur?: () => void;
  dataPrivate?: boolean;
}

const MIN_QUERY_LENGTH = 4;

const AddressAutocompleteSelect: React.FC<AddressAutocompleteSelectProps> = ({
  label,
  isRequired,
  errorText,
  value,
  onTextChange,
  onSelectSuggestion,
  onBlur,
  dataPrivate,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressOption[]>([]);
  const isFocusedRef = useRef(false);

  const debouncedLoadOptions = useCallback(
    debounce((inputVal: string, callback: (options: AddressOption[]) => void) => {
      const query = inputVal.trim();
      if (query.length < MIN_QUERY_LENGTH) {
        setSuggestions([]);
        callback([]);
        return;
      }
      fetch(
        `${process.env.REACT_APP_API_URL}/creditApplications/searchAddress?q=${encodeURIComponent(
          query
        )}`
      )
        .then((response) => response.json())
        .then((json: { data?: AddressSuggestion[] }) => {
          // Discard results that resolve after the field has already lost focus,
          // otherwise a late fetch reopens the suggestion menu over fields the
          // user has since moved on to.
          if (!isFocusedRef.current) {
            callback([]);
            return;
          }
          const options = (json?.data ?? []).map((suggestion) => {
            const streetLine = [suggestion.streetLine, suggestion.secondary]
              .filter(Boolean)
              .join(' ');
            const fullLabel = `${streetLine}, ${suggestion.city}, ${suggestion.state} ${suggestion.zipcode}`;
            return {
              ...suggestion,
              label: fullLabel,
              value: fullLabel,
            };
          });
          setSuggestions(options);
          callback(options);
        })
        .catch((error) => {
          console.error('Error fetching address suggestions: ', error);
          setSuggestions([]);
          callback([]);
        });
    }, 400),
    []
  );

  useEffect(() => () => debouncedLoadOptions.cancel(), [debouncedLoadOptions]);

  // Fully controlled instead of relying on react-select's own focus/click-driven open
  // state, so the menu only ever appears once a fresh-enough query has real matches,
  // and never reappears on refocus alone (suggestions are cleared on blur).
  const menuIsOpen = value.trim().length >= MIN_QUERY_LENGTH && suggestions.length > 0;

  return (
    <div className="w-full flex flex-col">
      {(label || isRequired) && (
        <label className="text-xs mb-0.5 text-left whitespace-nowrap font-medium">
          {isRequired && <span className="text-red-500">*</span>}
          {label}
        </label>
      )}
      <div
        className="input-wrapper h-fit relative"
        style={{ borderColor: errorText ? '#F04438' : isFocused ? '#3B82F6' : '#E4EAF1' }}
        {...(dataPrivate ? { 'data-private': true } : {})}>
        <AsyncCreatableSelect
          placeholder=""
          isClearable={false}
          loadOptions={debouncedLoadOptions}
          inputValue={value}
          menuIsOpen={menuIsOpen}
          onFocus={() => {
            isFocusedRef.current = true;
            setIsFocused(true);
          }}
          onBlur={() => {
            isFocusedRef.current = false;
            setIsFocused(false);
            setSuggestions([]);
            debouncedLoadOptions.cancel();
            onBlur?.();
          }}
          onChange={(newValue: any) => {
            if (!newValue) return;
            setSuggestions([]);
            debouncedLoadOptions.cancel();
            if (newValue.__isNew__) {
              onTextChange(newValue.value);
            } else {
              onSelectSuggestion({
                streetLine: newValue.streetLine,
                secondary: newValue.secondary,
                city: newValue.city,
                state: newValue.state,
                zipcode: newValue.zipcode,
              });
            }
          }}
          onInputChange={(inputVal: string, actionMeta: { action: string }) => {
            if (actionMeta.action === 'input-change') {
              setSuggestions([]);
              onTextChange(inputVal);
            }
          }}
          value={value ? { label: value, value } : null}
          styles={{
            control: (provided) => ({
              ...provided,
              minHeight: 'unset',
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
              cursor: 'text',
            }),
            valueContainer: (provided) => ({ ...provided, padding: 0 }),
            input: (provided) => ({
              ...provided,
              margin: 0,
              padding: 0,
              fontSize: '0.8em',
              input: { opacity: '1 !important' },
            }),
            singleValue: (provided) => ({ ...provided, fontSize: '0.8em', margin: 0 }),
            placeholder: (provided) => ({ ...provided, fontSize: '0.8em', margin: 0 }),
            indicatorsContainer: (provided) => ({ ...provided, padding: 0 }),
            menu: (provided) => ({ ...provided, zIndex: 20, fontSize: '0.8em' }),
          }}
          components={{
            IndicatorSeparator: () => null,
            DropdownIndicator: () => null,
            DownChevron: () => null,
          }}
          formatCreateLabel={(inputVal: string) => <>Use "{inputVal}"</>}
        />
      </div>
      {errorText && <p className="error-text">{errorText}</p>}
    </div>
  );
};

export default AddressAutocompleteSelect;
